import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDesktop,
    faEnvelopeOpenText,
    faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import NotificationSettingsContext from '../../contexts/NotificationsContext';
import classnames from 'classnames';

interface NotificationItem {
    id: number;
    name: string;
    description: string;
    has_custom_message: boolean;
    is_enabled: string[];
}

const BillingNotification: React.FC = () => {
    const settingsContext = useContext(NotificationSettingsContext);
    const { notificationSettings, updateTypeSettings, loading } = settingsContext;

    // Filter for billing-related notifications
    const billingNotifications = notificationSettings?.filter((notification: NotificationItem) =>
        notification.name.toLowerCase().includes('billing') ||
        notification.name.toLowerCase().includes('payment') ||
        notification.name.toLowerCase().includes('account')
    ) || [];
    const deliveryTypeIconMap = {
        'in-app': faDesktop,
        'email': faEnvelopeOpenText,
    };

    const getDeliveryTypeIcon = (key: string) => {
        if (!(key in deliveryTypeIconMap)) {
            throw new Error('No icon for Delivery Type');
        }

        return deliveryTypeIconMap[key as keyof typeof deliveryTypeIconMap];
    };

    const handleCheck = (notification: NotificationItem, key: string) => {
        let payload = [...notification.is_enabled];

        if (notification.is_enabled.includes(key)) {
            updateTypeSettings(
                notification.id,
                payload.filter((item) => item !== key),
                notification.has_custom_message ? '' : undefined,
            );
        } else {
            payload.push(key);
            updateTypeSettings(notification.id, payload, notification.has_custom_message ? '' : undefined);
        }
    };

    const isEnabled = (notification: NotificationItem, type: string): boolean => {
        return notification.is_enabled.includes(type);
    };

    const renderCheck = (notification: NotificationItem, key: string, index: number) => (
        <div key={index}>
            <label
                htmlFor={`billing-checkbox-${notification.id}-${key}`}
                className={classnames({
                    'check-holder': true,
                    disabled: loading,
                })}
                onClick={() => handleCheck(notification, key)}
                title={key.charAt(0).toUpperCase() + key.slice(1)}
            >
                <FontAwesomeIcon
                    icon={getDeliveryTypeIcon(key)}
                    id={`billing-checkbox-${notification.id}-${key}`}
                    className={classnames({
                        'notification-icon': true,
                        active: isEnabled(notification, key),
                        inactive: !isEnabled(notification, key),
                        selectable: !loading,
                    })}
                />
            </label>
        </div>
    );

    const options = ['in-app', 'email'];

    return (
        <div className="billing-notification-settings">
            <div className="sub-header">Billing Notifications</div>

            {billingNotifications.map((notification: any) => (
                <div key={notification.id} className="setting-control">
                    <div className="flex">
                        <div className="name">
                            {notification.name}
                            <span
                                className="help-icon"
                                title={notification.description}
                            >
                                <FontAwesomeIcon icon={faQuestionCircle} size="xs" />
                            </span>
                        </div>
                    </div>

                    <form className="flex">
                        {options.map((option, index) =>
                            renderCheck(notification, option, index)
                        )}
                    </form>
                </div>
            ))}

            {billingNotifications.length === 0 && !loading && (
                <div className="no-notifications">
                    No billing notifications available.
                </div>
            )}
        </div>
    );
};

export default BillingNotification;